/* Who is signed in, from GET /api/me. The session itself lives in httpOnly cookies the page never sees. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Me } from '@pathabroad/core/browser';
import { api, ApiError } from '../lib/api';
import type { Session } from '../lib/routes';
import { readLang } from '../lib/storage';
import { useT } from './i18n';

type Ctx = {
  session: Session;
  refresh: () => Promise<Me | null>;
  setMe: (me: Me) => void;
  patchMe: (f: (me: Me) => Me) => void;
  markOut: () => void;
  signOut: () => Promise<void>;
  /* shown once after the account is deleted */
  farewell: boolean;
  setFarewell: (on: boolean) => void;
};

const SessionContext = createContext<Ctx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { lang, setLang } = useT();
  const [session, setSession] = useState<Session>({ status: 'loading' });
  const [farewell, setFarewell] = useState(false);
  const langSynced = useRef(false);

  const setMe = useCallback((me: Me) => setSession({ status: 'in', me }), []);
  const markOut = useCallback(() => setSession({ status: 'out' }), []);
  const patchMe = useCallback((f: (me: Me) => Me) => setSession(s => (s.status === 'in' ? { status: 'in', me: f(s.me) } : s)), []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<Me>('/api/me');
      setSession({ status: 'in', me });
      return me;
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'signed_out' || e.status === 401)) { setSession({ status: 'out' }); return null; }
      /* keep a known session through a passing network error */
      setSession(s => (s.status === 'in' ? s : { status: 'error' }));
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post('/api/auth/signout'); } catch { /* the cookies are cleared server-side either way */ }
    setSession({ status: 'out' });
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  /* the account's language applies once per sign-in, unless this browser already has a chosen language */
  useEffect(() => {
    if (session.status !== 'in') { langSynced.current = false; return; }
    if (langSynced.current) return;
    langSynced.current = true;
    if (!readLang() && session.me.user.lang !== lang) setLang(session.me.user.lang, { remember: false });
  }, [session, lang, setLang]);

  const value = useMemo<Ctx>(() => ({ session, refresh, setMe, patchMe, markOut, signOut, farewell, setFarewell }),
    [session, refresh, setMe, patchMe, markOut, signOut, farewell]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Ctx {
  const c = useContext(SessionContext);
  if (!c) throw new Error('useSession outside SessionProvider');
  return c;
}

/* on pages behind the access guard, the student is signed in */
export function useMe(): Me {
  const { session } = useSession();
  if (session.status !== 'in') throw new Error('useMe on a page without a session');
  return session.me;
}
